import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageSEO from "@/components/base/PageSEO";
import ArticleHero from "./components/ArticleHero";
import ArticleContent from "./components/ArticleContent";
import RelatedArticles from "./components/RelatedArticles";
import CommentsSection from "./components/CommentsSection";
import ArticleSchema from "./components/ArticleSchema";
import { articleData, defaultArticles } from "@/mocks/articles";
import { categoryArticles, categoryInfo } from "@/mocks/categories";

const fallbackAuthorAvatar =
  "https://readdy.ai/api/search-image?query=Experienced%20journalist%20portrait%2C%20Chinese%20male%2C%20mature%20confident%20expression%2C%20warm%20studio%20lighting%2C%20simple%20cream%20background%2C%20professional%20editorial%20headshot&width=120&height=120&seq=author-fallback&orientation=squarish";

function findFallbackArticle(slug: string) {
  const categoryEntry = Object.entries(categoryArticles).find(([, articles]) =>
    articles.some((article) => article.slug === slug),
  );
  const categoryArticle = categoryEntry?.[1].find((article) => article.slug === slug);

  if (categoryArticle && categoryEntry) {
    const categorySlug = categoryEntry[0];
    return {
      slug: categoryArticle.slug,
      title: categoryArticle.title,
      category: categoryInfo[categorySlug]?.name || "娱乐八卦",
      categorySlug,
      date: categoryArticle.date,
      updatedDate: `${categoryArticle.date}T10:00:00+08:00`,
      views: categoryArticle.views,
      image: categoryArticle.image,
      author: "黑料网编辑部",
      authorAvatar: fallbackAuthorAvatar,
      excerpt: categoryArticle.excerpt,
      content: [
        {
          type: "paragraph" as const,
          text: categoryArticle.excerpt,
        },
        {
          type: "heading" as const,
          text: "事件背景",
        },
        {
          type: "paragraph" as const,
          text: `围绕“${categoryArticle.title}”的讨论持续升温，相关话题在社交平台和娱乐社区中快速扩散。黑料网根据公开信息和用户关注点，对事件脉络进行整理，方便读者快速了解核心进展。`,
        },
        {
          type: "heading" as const,
          text: "后续关注",
        },
        {
          type: "paragraph" as const,
          text: "目前相关信息仍在持续更新中，本站将继续跟进更多可靠线索，并对关键时间线、当事方回应和网友反馈进行补充。",
        },
      ],
      tags: [categoryInfo[categorySlug]?.name || "娱乐八卦", "黑料网", "热点爆料"],
      relatedArticles: defaultArticles
        .filter((article) => article.slug !== slug)
        .slice(0, 6)
        .map((article) => article.slug),
    };
  }

  const defaultArticle = defaultArticles.find((article) => article.slug === slug);
  if (!defaultArticle) return undefined;

  return {
    slug: defaultArticle.slug,
    title: defaultArticle.title,
    category: defaultArticle.category,
    categorySlug: defaultArticle.categorySlug,
    date: defaultArticle.date,
    updatedDate: `${defaultArticle.date}T10:00:00+08:00`,
    views: defaultArticle.views,
    image: defaultArticle.image,
    author: "黑料网编辑部",
    authorAvatar: fallbackAuthorAvatar,
    excerpt: defaultArticle.title,
    content: [
      {
        type: "paragraph" as const,
        text: defaultArticle.title,
      },
      {
        type: "paragraph" as const,
        text: "黑料网将持续整理该话题的关键线索、公开回应和后续进展，为读者提供清晰、及时的娱乐资讯参考。",
      },
    ],
    tags: [defaultArticle.category, "黑料网", "娱乐八卦"],
    relatedArticles: defaultArticles
      .filter((article) => article.slug !== slug)
      .slice(0, 6)
      .map((article) => article.slug),
  };
}

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>();

  const article = slug ? articleData[slug] : undefined;
  const fallbackArticle = slug ? findFallbackArticle(slug) : undefined;
  const displayArticle = article || fallbackArticle;

  if (!displayArticle) {
    return (
      <>
        <PageSEO
          title="文章不存在"
          description="这篇文章可能已经被删除，或者链接有误。访问黑料网查看更多娱乐八卦资讯。"
          keywords="黑料网,娱乐八卦,明星黑料"
          canonicalUrl="http://ijcbfgs.cn/"
        />
        <Navbar />
        <main className="pt-16 md:pt-18">
          <div className="max-w-[780px] mx-auto px-6 md:px-10 py-20 text-center">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center bg-background-100 rounded-full">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-3">
              文章不存在
            </h1>
            <p className="text-foreground-500 mb-8">
              这篇文章可能已经被删除，或者链接有误。
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 rounded-lg font-medium text-sm transition-colors duration-200 cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const pageUrl = `http://ijcbfgs.cn/article/${slug}`;

  return (
    <>
      <PageSEO
        title={displayArticle.title}
        description={displayArticle.excerpt}
        keywords={`${displayArticle.tags.join(",")},黑料网,娱乐八卦`}
        canonicalUrl={pageUrl}
        ogImage={displayArticle.image}
        ogType="article"
      />
      <ArticleSchema
        title={displayArticle.title}
        excerpt={displayArticle.excerpt}
        image={displayArticle.image}
        date={displayArticle.date}
        updatedDate={displayArticle.updatedDate}
        author={displayArticle.author}
        category={displayArticle.category}
        url={pageUrl}
      />
      <Navbar />
      <main className="pt-16 md:pt-18">
        <ArticleHero
          image={displayArticle.image}
          title={displayArticle.title}
          category={displayArticle.category}
          categorySlug={displayArticle.categorySlug}
          date={displayArticle.date}
          views={displayArticle.views}
          author={displayArticle.author}
          authorAvatar={displayArticle.authorAvatar}
        />
        <ArticleContent content={displayArticle.content} tags={displayArticle.tags} />
        <CommentsSection articleSlug={displayArticle.slug} />
        <RelatedArticles slugs={displayArticle.relatedArticles} />
      </main>
      <Footer />
    </>
  );
}
